<template>
  <div class="horizontal-scroll-container" ref="scrollContainer" :style="{
    paddingLeft: paddingX + 'px',
    paddingRight: paddingX + 'px'
  }">
    <!-- 左切换按钮 -->
    <button 
      class="scroll-control-btn scroll-left-btn"
      @click="scrollToPrevItem"
      :disabled="!autoPlay && displayedItems.length === 0"
    >
      <!-- ← -->
      ‹
    </button>

    <!-- 原有滚动视口 -->
    <div 
      class="horizontal-scroll-viewport" 
      ref="scrollViewport" 
      @mouseenter="pauseAutoScroll" 
      @mouseleave="resumeAutoScroll"
    >
      <div 
        class="horizontal-scroll-track" 
        :style="{ transform: `translateX(${scrollPosition}px)`, gap: gap + 'px' }"
      >
        <div 
          v-for="(item, idx) in displayedItems" 
          :key="idx" 
          class="scroll-item"
          :style="{ flex: '0 0 auto' }"
        >
          <slot 
            :item="item" 
            :index="idx % items.length" 
            :isClone="idx >= items.length"
          ></slot>
        </div>
      </div>
    </div>

    <!-- 右切换按钮 -->
    <button 
      class="scroll-control-btn scroll-right-btn"
      @click="scrollToNextItem"
      :disabled="!autoPlay && displayedItems.length === 0"
    >
      <!-- → -->
      ›
    </button>
  </div>
</template>

<script>
export default {
  name: 'HorizontalScroll',
  props: {
    /**
     * 滚动项数组
     */
    items: {
      type: Array,
      required: true
    },
    /**
     * 每项的宽度（像素），如果不设置则根据内容自适应
     */
    itemWidth: {
      type: Number,
      default: 0
    },
    /**
     * 项之间的间隙（像素）
     */
    gap: {
      type: Number,
      default: 30
    },
    /**
     * 两侧操作按钮间距
     */
    paddingX: {
      type: Number,
      default: 70
    },
    /**
     * 是否自动播放
     */
    autoPlay: {
      type: Boolean,
      default: true
    },
    /**
     * 步进滚动间隔时间（毫秒），默认3000ms
     */
    stepInterval: {
      type: Number,
      default: 3000
    }
  },
  data() {
    return {
      displayedItems: [],
      scrollPosition: 0,
      scrollTimer: null,
      isPaused: false,
      itemWidthCalculated: 0
    };
  },
  mounted() {
    this.initializeItems();
    this.$nextTick(() => {
      if (this.autoPlay) {
        this.startAutoScroll();
      }
    });
  },
  beforeDestroy() {
    this.clearScrollTimer();
  },
  watch: {
    items: {
      handler() {
        this.initializeItems();
        this.$nextTick(() => {
          this.scrollPosition = 0;
          this.restartAutoScroll();
        });
      },
      deep: true
    }
  },
  methods: {
    /**
     * 初始化显示项（复制数组以实现无缝滚动）
     */
    initializeItems() {
      this.displayedItems = [...this.items, ...this.items];
    },
    
    /**
     * 清除滚动定时器
     */
    clearScrollTimer() {
      if (this.scrollTimer) {
        clearInterval(this.scrollTimer);
        this.scrollTimer = null;
      }
    },
    
    /**
     * 重启自动滚动
     */
    restartAutoScroll() {
      this.clearScrollTimer();
      if (this.autoPlay && !this.isPaused) {
        this.startAutoScroll();
      }
    },
    
    /**
     * 开始步进式自动滚动（每3秒滚动一个item间距）
     */
    startAutoScroll() {
      this.scrollTimer = setInterval(() => {
        if (!this.autoPlay || this.isPaused) {
          return;
        }
        
        const singleItemTotalWidth = this.calculateSingleItemTotalWidth();
        if (singleItemTotalWidth <= 0) {
          return;
        }
        
        this.scrollPosition -= singleItemTotalWidth;
        this.resetScrollPosition();
      }, this.stepInterval);
    },

    /**
     * 重置滚动位置（实现无缝循环）
     */
    resetScrollPosition() {
      const singleSetWidth = this.calculateSingleSetWidth();
      if (singleSetWidth > 0 && this.scrollPosition <= -singleSetWidth) {
        this.scrollPosition = 0;
      } else if (this.scrollPosition > 0) {
        this.scrollPosition = 0;
      }
    },
    
    /**
     * 计算单个Item的总宽度（item宽度 + 间隙宽度）
     */
    calculateSingleItemTotalWidth() {
      if (this.itemWidth > 0) {
        return this.itemWidth + this.gap;
      } else {
        if (this.$refs.scrollViewport && this.displayedItems.length > 0) {
          const firstItem = this.$refs.scrollViewport.querySelector('.scroll-item');
          if (firstItem) {
            this.itemWidthCalculated = firstItem.offsetWidth;
            return this.itemWidthCalculated + this.gap;
          }
        }
        return 0;
      }
    },
    
    /**
     * 暂停自动滚动
     */
    pauseAutoScroll() {
      this.isPaused = true;
    },
    
    /**
     * 恢复自动滚动
     */
    resumeAutoScroll() {
      this.isPaused = false;
      this.restartAutoScroll();
    },
    
    /**
     * 计算单组数据的总宽度
     */
    calculateSingleSetWidth() {
      if (this.itemWidth > 0) {
        return this.items.length * (this.itemWidth + this.gap);
      } else {
        if (this.$refs.scrollViewport && this.displayedItems.length > 0) {
          const firstItem = this.$refs.scrollViewport.querySelector('.scroll-item');
          if (firstItem) {
            this.itemWidthCalculated = firstItem.offsetWidth;
            return this.items.length * (this.itemWidthCalculated + this.gap);
          }
        }
        return 0;
      }
    },

    /**
     * 手动切换到上一个item（左按钮）
     */
    scrollToPrevItem() {
      const singleItemTotalWidth = this.calculateSingleItemTotalWidth();
      if (singleItemTotalWidth <= 0) {
        return;
      }

      // 暂停自动滚动（手动操作时临时停止自动播放）
      const wasAutoPlay = this.autoPlay;
      this.autoPlay = false;
      this.clearScrollTimer();

      // 向右滚动（上一个item），即增加scrollPosition
      this.scrollPosition += singleItemTotalWidth;
      const singleSetWidth = this.calculateSingleSetWidth();
      
      // 处理左边界无缝逻辑
      if (singleSetWidth > 0 && this.scrollPosition >= 0) {
        this.scrollPosition = -singleSetWidth + singleItemTotalWidth;
      }

      // 延迟恢复自动播放（可选，保持手动操作后继续自动滚动）
      setTimeout(() => {
        this.autoPlay = wasAutoPlay;
        this.restartAutoScroll();
      }, 300);
    },

    /**
     * 手动切换到下一个item（右按钮）
     */
    scrollToNextItem() {
      const singleItemTotalWidth = this.calculateSingleItemTotalWidth();
      if (singleItemTotalWidth <= 0) {
        return;
      }

      // 暂停自动滚动（手动操作时临时停止自动播放）
      const wasAutoPlay = this.autoPlay;
      this.autoPlay = false;
      this.clearScrollTimer();

      // 向左滚动（下一个item），即减少scrollPosition
      this.scrollPosition -= singleItemTotalWidth;
      this.resetScrollPosition();

      // 延迟恢复自动播放（可选，保持手动操作后继续自动滚动）
      setTimeout(() => {
        this.autoPlay = wasAutoPlay;
        this.restartAutoScroll();
      }, 300);
    },
    
    /**
     * 手动设置滚动位置
     */
    scrollTo(position) {
      this.scrollPosition = position;
    },
    
    /**
     * 获取当前滚动位置
     */
    getScrollPosition() {
      return this.scrollPosition;
    }
  }
};
</script>

<style lang="less" scoped>
.horizontal-scroll-container {
  // padding: 0 70px;
  width: 100%;
  position: relative; // 为绝对定位的按钮提供参考
}

.horizontal-scroll-viewport {
  width: 100%;
  overflow: hidden;
  position: relative;
}

.horizontal-scroll-track {
  display: flex;
  align-items: center;
  width: max-content;
  // 平滑过渡效果，手动切换和自动滚动更流畅
  transition: transform 0.3s ease;
}

.scroll-item {
  flex: 0 0 auto;
}

// 左右切换按钮样式
.scroll-control-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: rgba(217, 228, 255, 0.3);
  box-shadow: 0 2 0 rgba(3, 3, 3, 0.1);
  cursor: pointer;
  z-index: 10; // 确保按钮在最上层
  font-size: 16px;
  color: #0248FF;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;

  &:hover {
     background: rgba(217, 228, 255, 0.5);
  }

  &:disabled {
    cursor: not-allowed;
    background: rgba(255, 255, 255, 0.3);
    color: #999;
  }
}

// 左按钮定位
.scroll-left-btn {
  left: 10px;
}

// 右按钮定位
.scroll-right-btn {
  right: 10px;
}
</style>